import React, { useRef } from "react";
import styled from "@emotion/styled";
import { Download, Printer } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

interface InvoiceProps {
  order: any;
}

const Invoice = ({ order }: InvoiceProps) => {
  const printRef = useRef<HTMLDivElement>(null);

  if (!order) return null;

  // Extract data
  const store = order?.items?.[0]?.product?.store || {};
  const customer = order?.customer || {};
  const shipping = order?.shippingInfos || {};
  const transaction = order?.transaction || {};
  const breakdown = order?.financialSummary?.breakdown || {};
  const financialSummary = order?.financialSummary || {};

  const paymentMethod =
    transaction.method?.toUpperCase() === "COD"
      ? "Cash On Delivery"
      : transaction.method || "N/A";
  const isPaid =
    transaction.status === "Paid" || transaction.status === "SUCCESS";

  const orderDate = order?.createdAt
    ? new Date(order.createdAt).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    : "N/A";

  const orderTime = order?.createdAt
    ? new Date(order.createdAt).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    : "";

  const customerAddress = [
    shipping.addressOne,
    shipping.addressTwo,
    shipping.city,
    shipping.state,
    shipping.zipcode,
  ]
    .filter(Boolean)
    .join(", ");

  const formatPrice = (val: number | undefined | null) => {
    const num = Number(val) || 0;
    return `Rs. ${num.toFixed(1)}`;
  };

  // Breakdown values
  const itemsPrice = order?.totalAmount || breakdown.originalAmount || 0;
  const activeDealDiscount = breakdown.activeDealDiscount || 0;
  const discountAmount = order?.discountAmount || 0;
  const couponDiscount = breakdown.couponDiscount?.total || 0;
  const processingPrice = order?.processingPrice || 0;
  const deliveryCharge = order?.deliveryCharge || breakdown.deliveryCharge || 0;
  const deliveryManTip = order?.deliveryManTip || breakdown.deliveryManTip || 0;
  const commissionCharge =
    order?.commissionCharge || breakdown.commissionCharge || 0;
  const totalAmount = order?.actualAmount || 0;

  const handlePrint = async () => {
    if (!printRef.current) return;

    try {
      const elementWidth = printRef.current.offsetWidth;

      const canvas = await html2canvas(printRef.current, {
        scale: 4,
        useCORS: true,
      });
      const imgData = canvas.toDataURL("image/png");

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Print Receipt</title>
              <style>
                @page { margin: 0; }
                body {
                  margin: 0;
                  display: flex;
                  justify-content: center;
                }
                img {
                  width: 100%;
                  max-width: ${elementWidth}px;
                  height: auto;
                  display: block;
                }
              </style>
            </head>
            <body>
              <img src="${imgData}" onload="window.print(); setTimeout(() => window.close(), 500);" />
            </body>
          </html>
        `);
        printWindow.document.close();
      }
    } catch (error) {
      console.error("Error printing invoice:", error);
    }
  };

  const handleDownload = async () => {
    if (!printRef.current) return;

    try {
      const elementWidth = printRef.current.offsetWidth;
      const elementHeight = printRef.current.offsetHeight;

      const canvas = await html2canvas(printRef.current, {
        scale: 4,
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");

      // Create PDF with the element's visual dimensions (1px = 1 unit)
      // This prevents the PDF from opening huge (2x/4x size)
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: [elementWidth, elementHeight],
      });

      pdf.addImage(imgData, "PNG", 0, 0, elementWidth, elementHeight);
      pdf.save(`invoice-${order?.id || "receipt"}.pdf`);
    } catch (error) {
      console.error("Error generating invoice PDF:", error);
    }
  };

  return (
    <ReceiptContainer>
      <ScrollArea>
        <ReceiptCard ref={printRef} id="print-area">
          {/* Platform Header */}
          <PlatformHeader>
            <PlatformLogo src="/logo1.png" alt="Zedfo" />
            <PlatformName>Zedfo Now Pvt. Ltd</PlatformName>
            <div
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <PlatformDetail>Kathmandu, Nepal</PlatformDetail>
              <PlatformDetail>•</PlatformDetail>
              <PlatformDetail>+977 9764640090</PlatformDetail>
            </div>
          </PlatformHeader>

          <Divider style={{ margin: "16px 0 20px" }} />

          {/* Store Information */}
          <SectionBlock>
            <SectionLabel>STORE INFORMATION</SectionLabel>
            <BoldName>
              {store.name || store.storeName || "Zedfo Store"}
            </BoldName>
            {(store.address || store.addressOne) && (
              <DetailText>
                Address: {store.address || store.addressOne}
              </DetailText>
            )}
            {store.phoneNo && <DetailText>Phone: {store.phoneNo}</DetailText>}
            {order?.module?.name && <ModuleTag>{order.module.name}</ModuleTag>}
          </SectionBlock>

          <Divider />

          {/* Customer Information */}
          <SectionBlock>
            <SectionLabel>CUSTOMER INFORMATION</SectionLabel>
            <BoldName>{customer.name || "Guest User"}</BoldName>
            {customerAddress && (
              <DetailText>Address: {customerAddress}</DetailText>
            )}
            {customer.phoneNo && (
              <DetailText>Phone: {customer.phoneNo}</DetailText>
            )}
          </SectionBlock>

          <Divider />

          {/* Order Meta Grid */}
          <MetaGrid>
            <MetaItem>
              <MetaLabel>Order ID:</MetaLabel>
              <MetaValue>#{order?.id}</MetaValue>
            </MetaItem>
            <MetaItem style={{ justifyContent: "flex-end" }}>
              <MetaLabel>Date:</MetaLabel>
              <MetaValue>{orderDate}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Order Type:</MetaLabel>
              <MetaValue>{shipping.type || "Delivery"}</MetaValue>
            </MetaItem>
            <MetaItem style={{ justifyContent: "flex-end" }}>
              <MetaLabel>Paid By:</MetaLabel>
              <MetaValue>{paymentMethod}</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Payment Status:</MetaLabel>
              <PaymentBadge
                isPaid={isPaid}
                isPartial={order?.paidAmount > 0 && order?.dueAmount > 0}
              >
                {order?.paidAmount > 0 && order?.dueAmount > 0
                  ? "Partially Paid"
                  : isPaid ? "Paid" : "Unpaid"}
              </PaymentBadge>
            </MetaItem>
            <MetaItem style={{ justifyContent: "flex-end" }}>
              <MetaLabel>Time:</MetaLabel>
              <MetaValue>{orderTime}</MetaValue>
            </MetaItem>
            {order?.trackingId && (
              <MetaItem
                style={{ gridColumn: "1 / -1", justifyContent: "flex-start" }}
              >
                <MetaLabel>Tracking ID:</MetaLabel>
                <MetaValue style={{ fontFamily: "monospace" }}>
                  {order.trackingId}
                </MetaValue>
              </MetaItem>
            )}
          </MetaGrid>

          <Divider />

          {/* Order Summary */}
          <SectionBlock>
            <SummaryTitle>ORDER SUMMARY</SummaryTitle>
            <ItemsList>
              {order?.items?.map((item: any, idx: number) => {
                const product = item?.product || {};
                const variation = item?.variation;
                const qty = item?.quantity || 1;
                const price = item?.actualAmount || item?.price || 0;
                const isAvailable = item?.isAvailable !== false;
                const alternative = item?.alternativeProduct;

                return (
                  <div key={idx}>
                    <ItemRow style={{ opacity: isAvailable ? 1 : 0.6 }}>
                      <ItemLeft>
                        <ItemQty>{qty}×</ItemQty>
                        <ItemInfo>
                          <ItemName>
                            {item.customName || product.name || "Product"}
                            {!isAvailable && (
                              <StatusTag style={{ background: "#fee2e2", color: "#dc2626" }}>
                                Unavailable
                              </StatusTag>
                            )}
                          </ItemName>
                          {variation && (
                            <ItemSub>
                              {variation.name || variation.type || ""}
                            </ItemSub>
                          )}
                          {item?.addons && item.addons.length > 0 && (
                            <ItemSub>
                              + {item.addons.map((a: any) => a.name).join(", ")}
                            </ItemSub>
                          )}
                        </ItemInfo>
                      </ItemLeft>
                      <ItemPrice>
                        {isAvailable ? formatPrice(price) : "Rs. 0"}
                      </ItemPrice>
                    </ItemRow>

                    {!isAvailable && alternative && (
                      <AlternativeRow>
                        <AltLabel>Suggested Alternative:</AltLabel>
                        <AltContent>
                          <AltName>{alternative.name || alternative.customName}</AltName>
                          <AltPrice>{formatPrice(alternative.price)}</AltPrice>
                        </AltContent>
                        <AltNote>Please add this item to the order to fulfill it.</AltNote>
                      </AlternativeRow>
                    )}
                  </div>
                );
              })}
            </ItemsList>
          </SectionBlock>

          <DashedDivider />

          {/* Price Breakdown */}
          <PriceSection>
            <PriceRow>
              <PriceLabel>Items Price</PriceLabel>
              <PriceVal>{formatPrice(itemsPrice)}</PriceVal>
            </PriceRow>
            {activeDealDiscount > 0 && (
              <PriceRow>
                <PriceLabel>Deal Discount</PriceLabel>
                <PriceDiscount>
                  -{formatPrice(activeDealDiscount)}
                </PriceDiscount>
              </PriceRow>
            )}
            {discountAmount > 0 && (
              <PriceRow>
                <PriceLabel>Discount</PriceLabel>
                <PriceDiscount>-{formatPrice(discountAmount)}</PriceDiscount>
              </PriceRow>
            )}
            {couponDiscount > 0 && (
              <PriceRow>
                <PriceLabel>Coupon Discount</PriceLabel>
                <PriceDiscount>-{formatPrice(couponDiscount)}</PriceDiscount>
              </PriceRow>
            )}
            {deliveryCharge > 0 && (
              <PriceRow>
                <PriceLabel>Delivery Charge</PriceLabel>
                <PriceVal>{formatPrice(deliveryCharge)}</PriceVal>
              </PriceRow>
            )}
            {deliveryManTip > 0 && (
              <PriceRow>
                <PriceLabel>Delivery Man Tip</PriceLabel>
                <PriceVal>{formatPrice(deliveryManTip)}</PriceVal>
              </PriceRow>
            )}
            {processingPrice > 0 && (
              <PriceRow>
                <PriceLabel>Platform Charge</PriceLabel>
                <PriceVal>{formatPrice(processingPrice)}</PriceVal>
              </PriceRow>
            )}
            {commissionCharge > 0 && (
              <PriceRow>
                <PriceLabel>Commission</PriceLabel>
                <PriceVal>{formatPrice(commissionCharge)}</PriceVal>
              </PriceRow>
            )}
          </PriceSection>

          <DashedDivider />

          {/* Total */}
          <TotalRow>
            <TotalLabel>Total Amount</TotalLabel>
            <TotalValue>{formatPrice(totalAmount)}</TotalValue>
          </TotalRow>

          {order?.paidAmount > 0 && (
            <>
              <DashedDivider style={{ margin: "12px 0" }} />
              <PriceRow>
                <PriceLabel style={{ fontWeight: 600, color: "#059669" }}>Amount Paid (Partial)</PriceLabel>
                <PriceVal style={{ fontWeight: 700, color: "#059669" }}>{formatPrice(order.paidAmount)}</PriceVal>
              </PriceRow>
              <PriceRow>
                <PriceLabel style={{ fontWeight: 600, color: "#dc2626" }}>Balance Due</PriceLabel>
                <PriceVal style={{ fontWeight: 700, color: "#dc2626" }}>{formatPrice(order.dueAmount)}</PriceVal>
              </PriceRow>
            </>
          )}

          {/* Earnings Summary */}
          {/* {(financialSummary.storeEarnings !== undefined ||
            financialSummary.adminEarnings !== undefined) && (
            <>
              <DashedDivider />
              <EarningsBlock>
                <EarningsTitle>EARNINGS SUMMARY</EarningsTitle>
                {financialSummary.storeEarnings !== undefined && (
                  <PriceRow>
                    <PriceLabel>Store Earnings</PriceLabel>
                    <PriceVal>
                      {formatPrice(financialSummary.storeEarnings)}
                    </PriceVal>
                  </PriceRow>
                )}
                {financialSummary.adminEarnings !== undefined && (
                  <PriceRow>
                    <PriceLabel>Admin Earnings</PriceLabel>
                    <PriceVal>
                      {formatPrice(financialSummary.adminEarnings)}
                    </PriceVal>
                  </PriceRow>
                )}
                {financialSummary.userSavings > 0 && (
                  <PriceRow>
                    <PriceLabel>Customer Savings</PriceLabel>
                    <PriceDiscount>
                      {formatPrice(financialSummary.userSavings)}
                    </PriceDiscount>
                  </PriceRow>
                )}
              </EarningsBlock>
            </>
          )} */}

          {/* Thank You */}
          <ThankYou>
            <ThankYouText>THANK YOU</ThankYouText>
            <Copyright>
              © {new Date().getFullYear()} Zedfo Now Pvt. Ltd. All rights
              reserved.
            </Copyright>
          </ThankYou>
        </ReceiptCard>
      </ScrollArea>

      {/* Action Buttons */}
      <ActionBar className="no-print">
        <DownloadBtn onClick={handleDownload}>
          <Download size={16} />
          Download
        </DownloadBtn>
        <PrintBtn onClick={handlePrint}>
          <Printer size={16} />
          Print
        </PrintBtn>
      </ActionBar>
    </ReceiptContainer>
  );
};

export default Invoice;

/* ==================== STYLED COMPONENTS ==================== */

const ReceiptContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  overflow: hidden;
  padding: 0px;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 4px;
  }
`;

const ReceiptCard = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 8px 10px;
  border: 1px solid #f0f0f0;
`;

/* Platform Header */
const PlatformHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`;

const PlatformLogo = styled.img`
  width: 52px;
  height: 52px;
  border-radius: 12px;
  object-fit: contain;
`;

const PlatformName = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #111;
  margin: 0;
`;

const PlatformDetail = styled.p`
  font-size: 13px;
  color: #777;
  margin: 0;
  line-height: 1.5;
`;

/* Sections */
const SectionBlock = styled.div`
  margin-bottom: 4px;
`;

const SectionLabel = styled.p`
  font-size: 11px;
  font-weight: 700;
  color: #ea580c;
  letter-spacing: 1px;
  margin: 0 0 8px 0;
  text-transform: uppercase;
`;

const BoldName = styled.h3`
  font-size: 17px;
  font-weight: 700;
  color: #111;
  margin: 0 0 4px 0;
`;

const DetailText = styled.p`
  font-size: 13px;
  color: #555;
  margin: 2px 0;
  line-height: 1.4;
`;

const ModuleTag = styled.span`
  display: inline-block;
  background: #eef2ff;
  color: #4f46e5;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 10px;
  margin-top: 6px;
`;

const Divider = styled.div`
  height: 1px;
  background: #f0f0f0;
  margin: 20px 0;
`;

const DashedDivider = styled.div`
  border-top: 1px dashed #e0e0e0;
  margin: 16px 0;
`;

/* Meta Grid */
const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MetaLabel = styled.p`
  font-size: 13px;
  color: #888;
  margin: 0;
`;

const MetaValue = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: #222;
  margin: 0;
`;

const PaymentBadge = styled.span<{ isPaid: boolean; isPartial?: boolean }>`
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 8px;
  margin-top: 2px;
  background: ${({ isPaid, isPartial }) =>
    isPartial ? "#fff7ed" : (isPaid ? "#ecfdf5" : "#fef2f2")};
  color: ${({ isPaid, isPartial }) =>
    isPartial ? "#c2410c" : (isPaid ? "#059669" : "#dc2626")};
`;

/* Order Summary */
const SummaryTitle = styled.p`
  font-size: 14px;
  font-weight: 700;
  color: #111;
  margin: 0 0 14px 0;
  letter-spacing: 0.3px;
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const ItemLeft = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  flex: 1;
`;

const ItemQty = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #ea580c;
  min-width: 24px;
`;

const ItemInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ItemName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #222;
`;

const ItemSub = styled.span`
  font-size: 12px;
  color: #888;
  margin-top: 2px;
`;

const ItemPrice = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #222;
  white-space: nowrap;
`;

/* Price Breakdown */
const PriceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PriceLabel = styled.span`
  font-size: 14px;
  color: #555;
`;

const PriceVal = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #222;
`;

const PriceDiscount = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #059669;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
`;

const TotalLabel = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #111;
`;

const TotalValue = styled.span`
  font-size: 22px;
  font-weight: 800;
  color: #ea580c;
`;

/* Earnings */
const EarningsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EarningsTitle = styled.p`
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 1px;
  margin: 0 0 4px 0;
`;

/* Thank You */
const ThankYou = styled.div`
  text-align: center;
  padding-top: 24px;
  margin-top: 24px;
  border-top: 1px solid #f0f0f0;
`;

const ThankYouText = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: #ea580c;
  letter-spacing: 3px;
  margin: 0 0 6px 0;
`;

const Copyright = styled.p`
  font-size: 11px;
  color: #aaa;
  margin: 0;
`;

/* Action Buttons */
const ActionBar = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 4px 4px;
  border-top: 1px solid #f0f0f0;
  background: #fff;
  flex-shrink: 0;
`;

const DownloadBtn = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background: #f9f9f9;
    border-color: #ccc;
  }
`;

const PrintBtn = styled.button`
  flex: 1.2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  background: #f23236;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    background: #d92b2f;
  }
`;

const StatusTag = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  margin-left: 8px;
  text-transform: uppercase;
`;

const AlternativeRow = styled.div`
  margin: 4px 0 12px 32px;
  padding: 8px;
  background: #f0f9ff;
  border-left: 2px solid #0ea5e9;
  border-radius: 4px;
`;

const AltLabel = styled.p`
  font-size: 11px;
  font-weight: 700;
  color: #0369a1;
  margin: 0 0 4px 0;
  text-transform: uppercase;
`;

const AltContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AltName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #0c4a6e;
`;

const AltPrice = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #0c4a6e;
`;

const AltNote = styled.p`
  font-size: 10px;
  color: #64748b;
  font-style: italic;
  margin: 4px 0 0 0;
`;
